import React from 'react';
import { useLoggedUser } from 'state/user';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useEventListener from 'hooks/use-event-listener';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import useFetchNodeFromCacheById from 'components/canvas/hooks/use-fetch-node-from-cache-by-id';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import useAuthorization from 'routes/surface/hooks/use-authorization';
import lockSurfaceNodeForUpdate from 'graphql/surface-node/mutations/lock-surface-node-for-update';
import unLockSurfaceNodeFromUpdate from 'graphql/surface-node/mutations/unlock-surface-node-from-update';
import { set } from 'lodash/fp';
import { SurfaceNodeUnion } from 'graphql/types';
import { NodeFormInitialValues } from 'types';

interface CanCreateOrEditSurfaceNode {
  canEdit: boolean;
  cannotEdit: boolean;
  canCreate: boolean;
  isUpdate: boolean;
  canCreateOrEdit: boolean;
}

export default function useCanCreateOrEditSurfaceNode(
  surfaceNode: SurfaceNodeUnion | NodeFormInitialValues,
): CanCreateOrEditSurfaceNode {
  const {
    canCreateSurfaceNode,
    canUpdateSurfaceNode,
  } = useCurrentSurfacePermissions();

  const {
    canUpdateSurfaceNode: canUpdateSurfaceNodeOnOriginal,
  } = useAuthorization();

  const saveNodeInCache = useSaveNodeInCache();

  const { id } = useCurrentSurface() || {};

  const fetchNodeFromCacheById = useFetchNodeFromCacheById(id);

  const [lockedState, setLockedState] = React.useState({
    isLocked: false,
    lockedBy: undefined,
  });

  const [loggedUser] = useLoggedUser();

  const isLocked = surfaceNode.isLocked || lockedState.isLocked;

  const lockedBy = surfaceNode.lockedBy || lockedState.lockedBy;

  const isUpdate = Boolean(surfaceNode.id);

  const canCreate = canCreateSurfaceNode();

  const isLockedByLoggedUser = lockedBy === loggedUser?.id;

  const isAllowedToEdit = surfaceNode?.originalNode?.surface
    ? canUpdateSurfaceNodeOnOriginal(surfaceNode?.originalNode?.surface)
    : canUpdateSurfaceNode(surfaceNode as SurfaceNodeUnion);

  const canEdit = isAllowedToEdit && isLockedByLoggedUser;

  const canCreateOrEdit = isUpdate ? canEdit : canCreate;

  const cannotEdit = canEdit === false && isUpdate;

  /*useSubscription(surfaceNodeUnLockedFromUpdateSubscription, {
    onSubscriptionData: (options) => {},
  });*/

  useEventListener('beforeunload', (event) => {
    event.preventDefault();
    event.returnValue = '';

    const isLockedByLoggedUser = isLocked && lockedBy === loggedUser?.id;

    if (isUpdate && isLockedByLoggedUser) {
      // Unlock
      unLockSurfaceNodeFromUpdate({ id: surfaceNode.id }).then((response) => {
        if (response.isSuccess) {
          setLockedState({ isLocked: false, lockedBy: undefined });
          // Set that node in cache is not locked
          unLockNodeInCache(surfaceNode.id);
        }
      });
    }

    return 'You have unsaved changes in your form. Are you sure you want to exit?';
  });

  React.useEffect(() => {
    const isLockedByLoggedUser = isLocked && lockedBy === loggedUser?.id;

    const isUpdate = Boolean(surfaceNode.id);

    if (isUpdate && isLocked === false) {
      lockSurfaceNodeForUpdate({ id: surfaceNode.id }).then((response) => {
        if (response.isSuccess) {
          // @ts-ignore
          setLockedState({ isLocked: true, lockedBy: loggedUser?.id });
        }
      });
    }

    return () => {
      if (isUpdate && isLockedByLoggedUser) {
        // Unlock
        unLockSurfaceNodeFromUpdate({ id: surfaceNode.id }).then((response) => {
          setLockedState({ isLocked: false, lockedBy: undefined });
          // Set that node in cache is not locked
          unLockNodeInCache(surfaceNode.id);
        });
      }
    };
  }, [surfaceNode.id, lockedBy, isLocked]);

  function unLockNodeInCache(id: string): void {
    const nodeFromCache = fetchNodeFromCacheById(id);

    if (nodeFromCache) {
      saveNodeInCache(set('isLocked', false, nodeFromCache));
    }
  }

  return { canEdit, cannotEdit, canCreate, isUpdate, canCreateOrEdit };
}
